import { useApplicationForm } from "@/context/ApplicationFormContext";
import { formatMoney } from "@/lib/application";
import { cn } from "@/lib/utils";

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}

function Field({ label, required, error, children, hint }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-[13px] font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

// All floors from 1 to 40
const ALL_FLOORS = Array.from({ length: 40 }, (_, i) => i + 1);

// Visual building component - modern futuristic design
interface BuildingVisualizerProps {
  floors: number | null;
  boilerRoomType: string | null;
  onFloorsChange: (floors: number) => void;
}

function BuildingVisualizer({ floors, boilerRoomType, onFloorsChange }: BuildingVisualizerProps) {
  const displayFloors = floors || 0;
  
  // Determine category
  const getCategory = () => {
    if (boilerRoomType) return { letter: "C", color: "emerald", amount: 300000 };
    if (displayFloors >= 17) return { letter: "C", color: "emerald", amount: 300000 };
    if (displayFloors >= 7) return { letter: "B", color: "blue", amount: 200000 };
    return { letter: "A", color: "amber", amount: 100000 };
  };
  
  const category = getCategory();
  const windowRows = Math.min(displayFloors, 7);
  const windowBaseY = 92;
  const windowStep = 10;

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left side - Floor selector */}
        <div className="flex flex-col justify-center items-start">
          <p className="text-slate-500 text-[12px] uppercase tracking-wider mb-4 text-left">
            Оберіть кількість поверхів
          </p>
          
          {/* All floors 1-40 inline */}
          <div className="grid grid-cols-8 gap-2">
            {ALL_FLOORS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onFloorsChange(n)}
                className={cn(
                  "w-10 h-10 rounded-xl text-[13px] font-semibold transition-all",
                  floors === n
                    ? "bg-primary text-white shadow-sm shadow-primary/30"
                    : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200"
                )}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Right side - Building visualization */}
        <div className="flex flex-col items-center justify-center p-4 border border-slate-200 rounded-2xl">
          {/* Floor count - above building */}
          {displayFloors > 0 && (
            <div className="mb-3 flex flex-col items-center text-slate-900">
              <span className="text-3xl font-semibold">{displayFloors}</span>
              <span className="text-[11px] uppercase tracking-wider text-slate-500">поверхів</span>
            </div>
          )}

          {displayFloors === 0 && (
            <div className="mb-3 flex items-center gap-2 text-[12px] text-slate-500">
              <svg
                className="h-4 w-4 text-slate-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v4" />
                <path d="M12 17v4" />
                <path d="M3 12h4" />
                <path d="M17 12h4" />
                <path d="M5.6 5.6l2.8 2.8" />
                <path d="M15.6 15.6l2.8 2.8" />
                <path d="M5.6 18.4l2.8-2.8" />
                <path d="M15.6 8.4l2.8-2.8" />
              </svg>
              <span>Оберіть кількість поверхів</span>
            </div>
          )}

          {/* Building display */}
          <div className="relative">
            {/* Glow effect */}
            <div 
              className={cn(
                "absolute inset-0 blur-3xl opacity-40 rounded-full",
                category.color === "emerald" && "bg-emerald-200/70",
                category.color === "blue" && "bg-blue-200/70",
                category.color === "amber" && "bg-amber-200/70"
              )}
            />
            
            {/* Building icon container */}
            <div className="relative w-40 h-48 flex flex-col items-center justify-end">
              {/* Boiler room indicator */}
              {boilerRoomType && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center justify-center px-3 py-1 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-[10px] font-medium text-red-600 uppercase tracking-wider leading-tight text-center">
                    {boilerRoomType === "roof" ? "Дахова" : boilerRoomType === "attached" ? "Прибудована" : "Вбудована"}<br/>котельня
                  </span>
                </div>
              )}
              
              {/* Building SVG */}
              <div className="relative">
                <svg 
                  viewBox="0 0 100 120" 
                  className={cn(
                    "w-32 h-40 transition-all duration-300",
                    category.color === "emerald" && "text-emerald-500",
                    category.color === "blue" && "text-blue-500",
                    category.color === "amber" && "text-amber-500"
                  )}
                  style={{ 
                    filter: `drop-shadow(0 0 20px ${category.color === "emerald" ? "#10b981" : category.color === "blue" ? "#3b82f6" : "#f59e0b"}40)`
                  }}
                >
                  {/* Building body */}
                  <rect x="15" y="18" width="70" height="100" rx="4" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2"/>
                  
                  {/* Windows - dynamic based on floors */}
                  {Array.from({ length: windowRows }).map((_, row) => (
                    <g key={row}>
                      {[0, 1, 2].map((col) => (
                        <rect
                          key={col}
                          x={25 + col * 20}
                          y={windowBaseY - row * windowStep}
                          width="12"
                          height="8"
                          rx="1"
                          fill="currentColor"
                          fillOpacity={0.6}
                        />
                      ))}
                    </g>
                  ))}
                  
                  {/* Roof */}
                  <path d="M10 20 L50 5 L90 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  
                  {/* Door */}
                  <rect x="40" y="104" width="20" height="14" rx="2" fill="currentColor" fillOpacity="0.3" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
          </div>
          
          {/* Category badge */}
          {displayFloors > 0 && (
            <div className="mt-4 flex items-center gap-3">
              <span 
                className={cn(
                  "text-lg font-bold",
                  category.color === "emerald" && "text-emerald-700",
                  category.color === "blue" && "text-blue-700",
                  category.color === "amber" && "text-amber-700"
                )}
              >
                Категорія {category.letter}
              </span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-900 font-semibold">до {formatMoney(category.amount)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function StepBuilding() {
  const { formData, updateBuilding } = useApplicationForm();
  const { building } = formData;

  return (
    <div className="space-y-6">
      {/* Building address */}
      <div>
        <h3 className="text-[15px] font-semibold mb-3">Адреса будинку</h3>
        <Field label="Повна адреса будинку" required hint="Адреса, для якої запитується допомога">
          <textarea
            value={building.building_address}
            onChange={(e) => updateBuilding({ building_address: e.target.value })}
            placeholder="м. Київ, вул. Хрещатик, 1"
            rows={2}
            className="w-full px-4 py-3 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
          />
        </Field>
      </div>

      {/* Building visualization */}
      <div>
        <h3 className="text-[15px] font-semibold mb-3">Поверховість будинку</h3>
        <p className="text-[12px] text-muted-foreground mb-4">
          У випадку різної поверховості, вказується максимальна
        </p>
        <BuildingVisualizer
          floors={building.floors_count}
          boilerRoomType={building.boiler_room_type}
          onFloorsChange={(floors) => updateBuilding({ floors_count: floors || null })}
        />
      </div>

      {/* Boiler room type selection */}
      <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
        <p className="text-[14px] font-medium mb-1">Наявність котельні</p>
        <p className="text-[12px] text-muted-foreground mb-4">
          Наявність котельні автоматично дає право на максимальну суму допомоги.
          <br />
          <span className="text-amber-600">Котельня повинна мати ту ж адресу, що й будинок.</span>
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => updateBuilding({ boiler_room_type: building.boiler_room_type === null ? null : null })}
            className={cn(
              "px-4 h-10 rounded-xl text-[13px] font-medium transition-all",
              building.boiler_room_type === null
                ? "bg-primary text-white shadow-sm"
                : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200"
            )}
          >
            Немає
          </button>
          <button
            type="button"
            onClick={() => updateBuilding({ boiler_room_type: "roof" })}
            className={cn(
              "px-4 h-10 rounded-xl text-[13px] font-medium transition-all",
              building.boiler_room_type === "roof"
                ? "bg-primary text-white shadow-sm"
                : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200"
            )}
          >
            Дахова
          </button>
          <button
            type="button"
            onClick={() => updateBuilding({ boiler_room_type: "attached" })}
            className={cn(
              "px-4 h-10 rounded-xl text-[13px] font-medium transition-all",
              building.boiler_room_type === "attached"
                ? "bg-primary text-white shadow-sm"
                : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200"
            )}
          >
            Прибудована
          </button>
          <button
            type="button"
            onClick={() => updateBuilding({ boiler_room_type: "builtin" })}
            className={cn(
              "px-4 h-10 rounded-xl text-[13px] font-medium transition-all",
              building.boiler_room_type === "builtin"
                ? "bg-primary text-white shadow-sm"
                : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200"
            )}
          >
            Вбудована
          </button>
        </div>
      </div>

      {/* Building details */}
      <div>
        <h3 className="text-[15px] font-semibold mb-4">Характеристики будинку</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Кількість співвласників" required>
            <input
              type="number"
              value={building.co_owners_count || ""}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                updateBuilding({ co_owners_count: isNaN(val) ? null : val });
              }}
              placeholder="120"
              min={1}
              className="w-full h-11 px-4 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </Field>

          <Field label="Кількість квартир" required>
            <input
              type="number"
              value={building.apartments_count || ""}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                updateBuilding({ apartments_count: isNaN(val) ? null : val });
              }}
              placeholder="100"
              min={1}
              className="w-full h-11 px-4 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </Field>

          <Field label="Загальна площа (м²)" required>
            <input
              type="number"
              value={building.total_area || ""}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                updateBuilding({ total_area: isNaN(val) ? null : val });
              }}
              placeholder="5000"
              min={0}
              step={0.1}
              className="w-full h-11 px-4 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </Field>
        </div>
      </div>

      {/* Document type selection */}
      <div>
        <h3 className="text-[15px] font-semibold mb-3">Документ для підтвердження поверховості</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => updateBuilding({ has_tech_passport: true })}
            className={cn(
              "p-4 rounded-xl border-[3px] text-left transition-all",
              building.has_tech_passport
                ? "border-primary bg-primary/5"
                : "border-border/50 hover:border-foreground/20"
            )}
          >
            <p className={cn("text-[13px] font-medium", building.has_tech_passport && "text-primary")}>
              Технічний паспорт
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Копія сторінок з адресою та поверховістю
            </p>
          </button>

          <button
            type="button"
            onClick={() => updateBuilding({ has_tech_passport: false })}
            className={cn(
              "p-4 rounded-xl border-[3px] text-left transition-all",
              !building.has_tech_passport
                ? "border-primary bg-primary/5"
                : "border-border/50 hover:border-foreground/20"
            )}
          >
            <p className={cn("text-[13px] font-medium", !building.has_tech_passport && "text-primary")}>
              Довідка про поверховість
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Від органу місцевого самоврядування
            </p>
          </button>
        </div>
      </div>

    </div>
  );
}
